import { ApolloError } from '@apollo/client';
import { Alert, Card, Center, Skeleton, VStack } from '@chakra-ui/react';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { useTranslate } from '@tolgee/react';
import { ChartData, ChartOptions } from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import useReservationHourlyDistribution from '../logic/use-hour-distribution';

export default function ReservationHourDistributionReport(props: { startTime: string; endTime: string }) {
  const branch = useFocusedBranchContext();
  const { t } = useTranslate();

  const { data, loading, error } = useReservationHourlyDistribution(
    branch.branch?.id ?? '',
    props.startTime,
    props.endTime,
  );

  const [chartData, setChartData] = useState<ChartData<'line'>>({ labels: [], datasets: [] });

  useEffect(() => {
    if (!data) {
      return;
    }

    const hourDistributionData = data.analytic_reservation_hourlyDistribution.slice();
    const allHours = Array.from({ length: 24 }, (_, index) => index);
    const allHoursLabels = Array.from({ length: 24 }, (_, index) => `${index.toString()}h`);

    const trends = allHours.map((hour) => {
      const distributionData = hourDistributionData.find((d) => d.hour === hour);
      return distributionData ? distributionData.reservationCount : 0;
    });

    let firstNonZeroIndex = -1;
    for (let i = 0; i < trends.length; i++) {
      if (trends[i] !== 0) {
        firstNonZeroIndex = i;
        break;
      }
    }

    if (firstNonZeroIndex === -1) {
      // All zeros, set empty chart data
      setChartData({ labels: [], datasets: [] });
      return;
    }

    let lastNonZeroIndex = -1;
    for (let i = trends.length - 1; i >= 0; i--) {
      if (trends[i] !== 0) {
        lastNonZeroIndex = i;
        break;
      }
    }

    // Determine the start index for slicing.
    // The original logic implied including the element *before* the first non-zero if it exists.
    // If firstNonZeroIndex is 0, it should start at 0.
    const chartStartIndex = Math.max(0, firstNonZeroIndex - 1);

    // Determine the end index for slicing (exclusive).
    // The original logic removes trailing zeros. So we slice up to the last non-zero element, plus one.
    const chartEndIndex = lastNonZeroIndex + 1;

    const displayedTrends = trends.slice(chartStartIndex, chartEndIndex);
    const displayedLabels = allHoursLabels.slice(chartStartIndex, chartEndIndex);

    setChartData({
      labels: displayedLabels,
      datasets: [
        {
          label: t('reservations', 'reservations'),
          data: displayedTrends,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          cubicInterpolationMode: 'monotone',
        },
      ],
    });
  }, [data, setChartData, t]);

  if (loading) {
    return (
      <VStack gap={4} align="stretch">
        <Skeleton height="40px" />
        <Skeleton height="300px" />
      </VStack>
    );
  }

  if (error && error instanceof ApolloError) {
    return (
      <>
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title fontWeight={'semibold'}>{t('Reservation distribution by hour error')}</Alert.Title>
            <Alert.Description>{error.message}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      </>
    );
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        grid: {
          display: false,
        },
        beginAtZero: true,
      },
      x: {
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: t('hours', 'hours'),
        },
      },
    },
  };

  return (
    <Card.Root>
      <Card.Header p={0}>
        <Card.Title fontWeight={'semibold'}>{t('Reservation distribution by hour')}</Card.Title>
      </Card.Header>
      <Card.Body p={0}>
        <Center rounded={'xl'} mt={4} p={4} w="full" bgColor={'gray.50'} border={'1px solid'} borderColor={'gray.200'}>
          <Line options={options} data={chartData} />
        </Center>
      </Card.Body>
    </Card.Root>
  );
}
